import { CommonChartProps } from '../../types';
import { Area, AreaProps, AreaChart as RechartsAreaChart } from 'recharts';
import { BaseChart } from '../../BaseChart';
import React, { type JSX } from 'react';
import { getAdjustedOpacity } from '../../utils';

export interface GradientProps {
  gradient?: boolean;
  gradientStartOpacity?: number;
  gradientStopOpacity?: number;
}

export type ExtendedAreaProps = React.PropsWithoutRef<
  Omit<AreaProps<any, any>, 'ref'>
> &
  GradientProps;

export type AreaChartProps<TData> = CommonChartProps<
  TData,
  React.ComponentProps<typeof RechartsAreaChart> & { data?: TData[] },
  ExtendedAreaProps
>;

export const AreaChart = <TData,>(
  props: AreaChartProps<TData>,
): JSX.Element => {
  return (
    <BaseChart
      {...props}
      ChartComponent={RechartsAreaChart}
      chartType="area"
      renderSeries={({ normalizedSeries, activeIndex }) =>
        normalizedSeries.map(({ dataKey, color, ...seriesProps }, index) => {
          const {
            gradient,
            gradientStartOpacity = 0.15,
            gradientStopOpacity = 0.03,
          } = seriesProps;

          const gradientId = getGradientId(dataKey, color || '');

          return (
            <React.Fragment key={dataKey}>
              <Area
                stroke={color}
                fill={gradient ? `url(#${gradientId})` : color}
                {...(seriesProps as any)}
                dataKey={dataKey}
                key={dataKey}
                strokeOpacity={getAdjustedOpacity(
                  activeIndex,
                  index,
                  seriesProps.strokeOpacity,
                )}
                fillOpacity={getAdjustedOpacity(
                  activeIndex,
                  index,
                  gradient ? 1 : seriesProps.fillOpacity,
                )}
              />
              {gradient &&
                renderGradientDef(
                  dataKey,
                  color || '',
                  gradientStartOpacity,
                  gradientStopOpacity,
                )}
            </React.Fragment>
          );
        })
      }
    />
  );
};

const getGradientId = (dataKey: string, color: string) =>
  `gradient-${(dataKey + color).replace(/[^a-zA-Z0-9_]/g, '-')}`;

// Convert GradientDef to a render function
const renderGradientDef = (
  dataKey: string,
  color: string,
  gradientStartOpacity: number,
  gradientStopOpacity: number,
) => {
  const gradientId = getGradientId(dataKey, color || '');
  return (
    <defs>
      <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
        <stop
          offset="5%"
          stopColor={color}
          stopOpacity={gradientStartOpacity}
        />
        <stop
          offset="95%"
          stopColor={color}
          stopOpacity={gradientStopOpacity}
        />
      </linearGradient>
    </defs>
  );
};
